/**
 * Черновик FAQ для сайта из вопросов клиентов (#843, спека
 * docs/plans/2026-10-02-customer-questions-and-decline-reasons.md §8 п. 6 и
 * §9 FAQ1–FAQ3) — вкладка «Сайт и контент».
 *
 * По нажатию «Собрать черновик», за окно и проекты, выбранные над вкладкой
 * (`requestKey` — ключ запроса раздела). Черновик не хранится на сервере: он
 * живёт в памяти вкладки, смена фильтра его прячет, возврат к окну показывает
 * снова, повторное нажатие строит заново. Пометка «Сгенерировано» стоит на самом черновике; ответы —
 * заготовки, которые клиент заполняет и проверяет.
 *
 * Показывается только при открытом флаге `enableCustomerQuestions` — его
 * рендерит `VoiceSiteQuestions`; сервер проверяет тот же флаг сам.
 *
 * Под фильтром темы (#2229, спека 2026-10-11-voice-report-layers.md §3)
 * черновик собирается по вопросам разговоров темы (`themeCode` в теле). Ответ
 * без эха этой темы (edge-api до #2098) помечен «собран без фильтра темы», а
 * не выдаётся за FAQ темы; отказ фильтра (403/400) снимает тему, как у блоков.
 */
import { useContext, useEffect, useState } from 'react';
import {
  skipToken,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, Copy, FileText, Sparkles } from 'lucide-react';
import {
  VOICE_FAQ_DAILY_LIMIT,
  VOICE_FAQ_USER_DAILY_LIMIT,
  type VoiceFaqDraft as VoiceFaqDraftData,
  type VoiceFaqDraftResponse,
} from '@aica/api-types/schemas';
import { DashboardPanel } from '@/components/dashboard/dashboard-panel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { useLockedDemoReason } from '@/contexts/LockedDemoContext';
import { apiErrorBody } from '@/lib/fetch-utils';
import type { ReportsApi } from '@/lib/api/domains/reports.api';
import { formatDateShort } from '@/lib/utils';
import { VoiceThemeRefusalContext } from './voice-theme-filter-context';
import { themeFilterRefusalCode } from './voice-theme-filter-view-model';

type DraftParams = Parameters<ReportsApi['generateVoiceFaqDraft']>[0];

/** Исход нажатия: ответ сервера или код его отказа. */
type DraftOutcome =
  | { kind: 'response'; response: VoiceFaqDraftResponse }
  | { kind: 'error'; code: string | null };

type ReadyDraft = Extract<VoiceFaqDraftResponse, { status: 'ready' }>;

/** Исход нажатия — у того окна и той области, для которых его строили. */
interface Keyed<T> {
  key: readonly unknown[];
  value: T;
}

/** Последний черновик и исход последнего нажатия одного окна и области. */
interface FaqDraftResult {
  draft: ReadyDraft | null;
  outcome: DraftOutcome | null;
}

/** Ключ i18n сообщения об отказе по `code` ответа (`VOICE_FAQ_DRAFT_ERROR_CODES`). */
function errorKey(code: string | null): string {
  switch (code) {
    case 'over_limit':
    case 'over_limit_day':
      return 'voicePage.site.faq.errors.overLimit';
    case 'over_limit_hour':
      return 'voicePage.site.faq.errors.overLimitHour';
    case 'over_limit_minute':
      return 'voicePage.site.faq.errors.overLimitMinute';
    case 'over_user_limit':
      return 'voicePage.site.faq.errors.overUserLimit';
    case 'temporarily_unavailable':
    case 'draft_unavailable':
      return 'voicePage.site.faq.errors.temporarilyUnavailable';
    case 'tier_not_eligible':
    case 'feature_disabled':
      return 'voicePage.site.faq.errors.unavailable';
    case 'draft_timeout':
      return 'voicePage.site.faq.errors.timeout';
    default:
      return 'voicePage.site.faq.errors.failed';
  }
}

const LIMITS = { limit: VOICE_FAQ_DAILY_LIMIT, userLimit: VOICE_FAQ_USER_DAILY_LIMIT };

/**
 * One key for every build of an organization: a block remounted while a build
 * is under way (a new theme loads, #2229) still sees it and keeps the button
 * disabled; another organization's build does not.
 */
const faqDraftMutationKey = (orgId: string | null | undefined) =>
  ['voice', 'faq-draft', orgId ?? null] as const;

/**
 * Where the result of a window lives: in the query cache, not in the block's
 * state — the block unmounts while a new theme or window loads (#2229), and a
 * draft already paid for comes back with its window. Outside the `voice`
 * prefix the theme-set invalidation resets. `key` is the section's request
 * key, organization included; `themeRevision` — the theme's revision the
 * draft was built under (#2238): a new definition or base changes the theme's
 * conversations, and a draft of the old ones is not shown under the same name.
 */
const faqDraftResultKey = (key: string, themeRevision: string | null) =>
  ['voice-faq-draft-result', key, themeRevision] as const;

/** Текст черновика для буфера обмена — с пометкой «сгенерировано» в первой строке. */
function faqDraftClipboardText(draft: VoiceFaqDraftData, header: string): string {
  return [
    header,
    ...draft.items.map((item) => `${item.question}\n${item.answerStub}`),
  ].join('\n\n');
}

export interface VoiceFaqDraftProps {
  params: DraftParams;
  /** Ключ запроса раздела: окно, проекты, категория, демо, организация. */
  requestKey: readonly unknown[];
  /** Новый фильтр ещё грузится — собирать по нему рано. */
  disabled?: boolean;
  /**
   * Ревизия выбранной темы (`VoiceThemeFilterTheme.revision`): правка её
   * определения или базы — в этой сессии или в другой, с перечитыванием строк
   * блока тем — прячет черновик, собранный по прежнему составу (#2238).
   */
  themeRevision?: string;
}

export function VoiceFaqDraft({
  params,
  requestKey,
  disabled,
  themeRevision,
}: VoiceFaqDraftProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  const { apiClient, orgId } = useAuth();
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const onThemeRefused = useContext(VoiceThemeRefusalContext);
  // The draft is a paid LLM call: on the demo of a closed section it stays
  // closed, and the panel says which plan opens it (#2816).
  const lockedReason = useLockedDemoReason();

  // The draft and the last outcome belong to the window and scope they were
  // built for: a new filter (or another organization) hides them rather than
  // showing them under the new name, and going back shows them again.
  const keyString = JSON.stringify(requestKey);
  const revision = themeRevision ?? null;
  useEffect(() => {
    setCopyState('idle');
  }, [keyString]);
  const queryClient = useQueryClient();
  const { data: result } = useQuery<FaqDraftResult>({
    queryKey: faqDraftResultKey(keyString, revision),
    queryFn: skipToken,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const mutation = useMutation({
    mutationKey: faqDraftMutationKey(orgId),
    mutationFn: async (request: {
      body: DraftParams;
      key: readonly unknown[];
    }): Promise<Keyed<DraftOutcome>> => {
      try {
        return {
          key: request.key,
          value: {
            kind: 'response',
            response: await apiClient.reports.generateVoiceFaqDraft(request.body),
          },
        };
      } catch (error) {
        const errorBody = await apiErrorBody(error);
        // The filter's refusal is the section's to handle: it lifts the theme.
        if (request.body.themeCode !== undefined) {
          const refused = themeFilterRefusalCode({
            status: (error as { status?: unknown } | null)?.status,
            data: errorBody,
          });
          if (refused !== null) onThemeRefused?.(refused, request.body.themeCode);
        }
        return {
          key: request.key,
          value: {
            kind: 'error',
            code: typeof errorBody?.code === 'string' ? errorBody.code : null,
          },
        };
      }
    },
    // Runs even after the block unmounted: the result is filed under the
    // window it was built for, never shown under another.
    onSuccess: ({ key, value: outcome }) => {
      queryClient.setQueryData<FaqDraftResult>(key, (previous) => ({
        outcome,
        // A rebuild replaces the draft; a window that now has nothing to
        // build from drops it. A failed rebuild keeps the previous draft.
        draft:
          outcome.kind === 'response'
            ? outcome.response.status === 'ready'
              ? outcome.response
              : null
            : (previous?.draft ?? null),
      }));
    },
  });

  // No reset on a filter change: a build already paid for keeps the button
  // disabled until it ends, so a second paid call cannot start meanwhile —
  // even after the block was remounted under a new theme.
  const building = useIsMutating({ mutationKey: faqDraftMutationKey(orgId) }) > 0;
  const build = () => {
    setCopyState('idle');
    // The previous refusal is not the answer to this press; the previous
    // draft stays on screen until the new one replaces it.
    queryClient.setQueryData<FaqDraftResult>(
      faqDraftResultKey(keyString, revision),
      (previous) => ({
        draft: previous?.draft ?? null,
        outcome: null,
      })
    );
    mutation.mutate({ body: params, key: faqDraftResultKey(keyString, revision) });
  };

  const draft = result?.draft ?? null;
  // Built under a theme, answered without its echo: not the theme's FAQ.
  const builtWithoutTheme =
    params.themeCode !== undefined &&
    draft !== null &&
    draft.themeFilter?.code !== params.themeCode;
  const outcome = result?.outcome ?? null;

  const copy = async (data: VoiceFaqDraftData) => {
    try {
      await navigator.clipboard.writeText(
        faqDraftClipboardText(data, t('voicePage.site.faq.copyHeader'))
      );
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  return (
    <DashboardPanel
      title={t('voicePage.site.faq.title')}
      action={
        <Button
          variant={draft ? 'outline' : 'primary'}
          size="sm"
          onClick={build}
          isLoading={building}
          disabled={disabled || building || lockedReason !== undefined}
          title={lockedReason}
        >
          {!building && <Sparkles className="h-4 w-4" />}
          {building
            ? t('voicePage.site.faq.building')
            : draft
              ? t('voicePage.site.faq.rebuild')
              : t('voicePage.site.faq.build')}
        </Button>
      }
    >
      <div className="space-y-4">
        {!draft && (
          <div className="space-y-1 text-sm text-fg-muted">
            <p className="max-w-prose">{t('voicePage.site.faq.intro')}</p>
            <p className="text-xs text-fg-subtle">
              {t('voicePage.site.faq.limit', LIMITS)}
            </p>
            {lockedReason && (
              <p className="text-xs text-fg-muted" data-testid="voice-faq-locked">
                {lockedReason}
              </p>
            )}
          </div>
        )}

        {outcome?.kind === 'error' && (
          <p role="alert" className="text-sm text-danger">
            {t(errorKey(outcome.code), LIMITS)}
          </p>
        )}

        {outcome?.kind === 'response' && outcome.response.status === 'no_questions' && (
          <p className="text-sm text-fg-muted">{t('voicePage.site.faq.noQuestions')}</p>
        )}

        {outcome?.kind === 'response' &&
          outcome.response.status === 'no_usable_wordings' && (
            <p className="text-sm text-fg-muted">
              {outcome.response.calls !== undefined
                ? t('voicePage.site.faq.noUsableWordingsCount', {
                    calls: outcome.response.calls,
                  })
                : t('voicePage.site.faq.noUsableWordings')}
            </p>
          )}

        {outcome?.kind === 'response' && outcome.response.status === 'no_draft' && (
          <p className="text-sm text-fg-muted">{t('voicePage.site.faq.noDraft')}</p>
        )}

        {draft && (
          <article
            aria-label={t('voicePage.site.faq.title')}
            className="space-y-4 rounded-md border border-border bg-bg-subtle p-4"
          >
            <header className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <Badge
                  variant="warning"
                  size="sm"
                  icon={<FileText className="h-3 w-3" />}
                >
                  {t('voicePage.site.faq.generated')}
                </Badge>
                <p className="max-w-prose text-xs text-fg-muted">
                  {t('voicePage.site.faq.generatedNote', {
                    date: formatDateShort(draft.draft.generatedAt),
                  })}
                </p>
                <p className="text-xs text-fg-subtle">
                  {t('voicePage.site.faq.source', {
                    subjects: draft.source.subjects,
                    calls: draft.source.calls,
                  })}
                </p>
                {builtWithoutTheme && (
                  <p className="text-xs text-warning">
                    {t('voicePage.themeFilter.draftWithoutTheme')}
                  </p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => void copy(draft.draft)}>
                {copyState === 'copied' ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                {copyState === 'copied'
                  ? t('voicePage.site.faq.copied')
                  : t('voicePage.site.faq.copy')}
              </Button>
            </header>
            {copyState === 'failed' && (
              <p role="alert" className="text-xs text-danger">
                {t('voicePage.site.faq.copyFailed')}
              </p>
            )}
            <ol className="space-y-3">
              {draft.draft.items.map((item, index) => (
                <li
                  key={`${item.subjectCode}-${index}`}
                  className="space-y-1 border-t border-border pt-3 first:border-t-0 first:pt-0"
                >
                  <p className="text-sm font-medium text-fg">
                    <span className="sr-only">{t('voicePage.site.faq.question')}: </span>
                    {item.question}
                  </p>
                  <p className="text-sm text-fg-muted whitespace-pre-line">
                    <span className="sr-only">{t('voicePage.site.faq.answer')}: </span>
                    {item.answerStub}
                  </p>
                </li>
              ))}
            </ol>
          </article>
        )}
      </div>
    </DashboardPanel>
  );
}
